import { useBreakpointValue } from '@genial/utils';
import { useRecoilValue } from 'recoil';

import { customerItemState } from '../../../store/customer/atom';
import { useProductActions } from '../../../store/product/actions';
import { productItemState, productStatusState } from '../../../store/product/atom';
import { hasProductItem } from '../../../store/product/selectors';
import { AssetSelectedTypeMap, SelectedProduct } from '../../../store/product/types';
import { CoesItemSelected } from '../../BatchTicket/components/DynamicAsset/components/Asset/products/Coes/ItemSelected';
import { DirectTreasuryItemSelected } from '../../BatchTicket/components/DynamicAsset/components/Asset/products/DirectTreasury/ItemSelected';
import { DirectTreasuryItemSellSelected } from '../../BatchTicket/components/DynamicAsset/components/Asset/products/DirectTreasurySell/ItemSelected';
import { EquityItemSelected } from '../../BatchTicket/components/DynamicAsset/components/Asset/products/Equity/ItemSelected';
import { EquityItemSellSelected } from '../../BatchTicket/components/DynamicAsset/components/Asset/products/EquitySell/ItemSelected';
import { FixedIncomeItemSelected } from '../../BatchTicket/components/DynamicAsset/components/Asset/products/FixedIncome/ItemSelected';
import { FixedIncomeItemSellSelected } from '../../BatchTicket/components/DynamicAsset/components/Asset/products/FixedIncomeSell/ItemSelected';
import { FundsItemSelected } from '../../BatchTicket/components/DynamicAsset/components/Asset/products/Funds/ItemSelected';
import { FundsItemSellSelected } from '../../BatchTicket/components/DynamicAsset/components/Asset/products/FundsSell/ItemSelected';
import { getProductColorFromType } from '../../BatchTicket/components/DynamicAsset/constants';

import { MobileAssetSelected } from './MobileAssetSelected';

export const DynamicAssetSelected = () => {
  const isMobile = useBreakpointValue({ base: true, sm: false });
  const customer = useRecoilValue(customerItemState);
  const hasProduct = useRecoilValue(hasProductItem);
  const productItem = useRecoilValue(productItemState);
  const productStatus = useRecoilValue(productStatusState);
  const { handleSelectProductItem, unselectProduct } = useProductActions();

  const handleUpdateProduct = (offer: AssetSelectedTypeMap[keyof AssetSelectedTypeMap]) => {
    handleSelectProductItem({
      ...productItem.current,
      ...offer,
    } as unknown as SelectedProduct);
  };

  const getOffer = <T extends keyof AssetSelectedTypeMap>() => {
    return productItem.current as unknown as AssetSelectedTypeMap[T];
  };

  if (!hasProduct) {
    return null;
  }

  if (isMobile && productStatus.status !== 'completed') {
    return null;
  }

  if (!isMobile && productStatus.status !== 'opened' && productStatus.status !== 'completed') {
    return null;
  }

  const equityOrderType =
    productItem.current.id === 'equity' ? productItem.current.orderType ?? 'Limit' : undefined;

  const baseProps = {
    key: `${productItem.current.id}-${productItem.current.orderQuantity}-${
      productItem.current.orderValue
    }-${productItem.current.operationType}-${equityOrderType ?? ''}`,
    onUpdate: handleUpdateProduct,
    hideLateralBar: isMobile,
    accountId: customer.current.accountId,
  };

  const getDynamicAssetSelectedComponent = () => {
    switch (productItem.current.id) {
      case 'fixedIncome':
        return <FixedIncomeItemSelected offer={getOffer<'fixedIncome'>()} {...baseProps} />;

      case 'fixedIncomeSell':
        return <FixedIncomeItemSellSelected offer={getOffer<'fixedIncomeSell'>()} {...baseProps} />;

      case 'funds':
        return <FundsItemSelected offer={getOffer<'funds'>()} {...baseProps} />;

      case 'fundsSell':
        return <FundsItemSellSelected offer={getOffer<'fundsSell'>()} {...baseProps} />;

      case 'equity':
        return (
          <EquityItemSelected offer={getOffer<'equity'>()} {...baseProps} isBatchTicket={false} />
        );

      case 'equitySell':
        return <EquityItemSellSelected offer={getOffer<'equitySell'>()} {...baseProps} />;

      case 'directTreasury':
        return <DirectTreasuryItemSelected offer={getOffer<'directTreasury'>()} {...baseProps} />;

      case 'directTreasurySell':
        return (
          <DirectTreasuryItemSellSelected offer={getOffer<'directTreasurySell'>()} {...baseProps} />
        );

      case 'coes':
        return <CoesItemSelected offer={getOffer<'coes'>()} {...baseProps} />;

      default:
        return null;
    }
  };

  if (isMobile) {
    return (
      <MobileAssetSelected
        productColor={getProductColorFromType(productItem.current.id)}
        handleEditProduct={unselectProduct}>
        {getDynamicAssetSelectedComponent()}
      </MobileAssetSelected>
    );
  }

  return getDynamicAssetSelectedComponent();
};
